import { ahuBoardApi } from 'app/services/ahuBoardApi';
import type { RootState } from 'app/store';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';

/**
 * Port of `ahu/mvc/model/tunnel.js:141-150` `recalc_length` — a tunnel's
 * `length` is the sum of its sections' lengths, recomputed ON THE CLIENT and
 * written back locally.
 *
 * WHY IT HAS TO BE CLIENT-SIDE. The canvas draws the cabinet shell from
 * `tunnel.length` (`AhuBoardPage.tsx` canvasTunnels → `canvas3dGeometry`
 * `getAhuSizes` and the `start_x` chain at :1170-1179) and the section bands
 * from the section rows. Move one without the other and the redraw is
 * internally inconsistent: the bands are the right size but the tunnels they
 * sit in are still positioned from the OLD lengths, so the stack goes ragged —
 * which is exactly what a page reload "fixed", since a reload refetches both.
 *
 * Legacy never has that window because `tunnel.js:91-117` listens on
 * `storage:section.collection:{sync,remove,change}` and re-sums on every one.
 * React's only equivalent lived inside the syncdata websocket listener, and
 * that path is DEAD for client-initiated edits: `patchAhuSection` writes the
 * new length into the section cache OPTIMISTICALLY, so by the time the
 * post_save echo arrives the listener's Backbone-style `changed` diff no
 * longer contains `length` and it returns before reaching the re-sum. Hence
 * this helper, called from the section mutations themselves.
 *
 * Summing is not an approximation: the server's `AHUTunnels.length` is the same
 * sum (`models_tunnel.py:83-94`, `round(float(length), 2)`), and legacy sets it
 * locally only — `length` is in the tunnel model's `readOnlyFields`
 * (`tunnel.js:32`), so this never becomes a PATCH ([NEG]#57).
 */

type UpdateTunnelsAction = ReturnType<typeof ahuBoardApi.util.updateQueryData>;

export interface TunnelLengthHost {
  dispatch: (action: UpdateTunnelsAction) => unknown;
  getState: () => unknown;
}

export const sumSectionLengths = (rows: IAhuBoardSection[]): number =>
  Math.round(rows.reduce((sum, row) => sum + (Number(row.length) || 0), 0) * 100) / 100;

/**
 * The `getAhuTunnelList` cache entry that holds `tunnelId`, plus the ahuId it
 * was fetched with (the `updateQueryData` arg). Same query-scan idiom as
 * `boardActions.ts` getCachedTunnels — a board only ever holds one entry.
 */
export const findTunnelListEntry = (
  state: RootState,
  tunnelId: number
): { ahuId: number; tunnels: IAhuBoardTunnel[] } | null => {
  const queries = (
    state[ahuBoardApi.reducerPath] as unknown as {
      queries: Record<
        string,
        { endpointName?: string; originalArgs?: unknown; data?: unknown } | undefined
      >;
    }
  ).queries;
  for (const q of Object.values(queries)) {
    if (q?.endpointName !== 'getAhuTunnelList' || !Array.isArray(q.data)) continue;
    const tunnels = q.data as IAhuBoardTunnel[];
    if (!tunnels.some((t) => t.id === tunnelId)) continue;
    const ahuId = Number(q.originalArgs);
    if (Number.isFinite(ahuId)) return { ahuId, tunnels };
  }
  return null;
};

/**
 * Re-sum `tunnelId`'s sections out of the RTKQ cache and write the result onto
 * its row in `getAhuTunnelList`. No-op when the sum is unchanged or either
 * cache entry is missing.
 */
export function recalcTunnelLength(
  { dispatch, getState }: TunnelLengthHost,
  tunnelId: number
): void {
  const state = getState() as RootState;
  const rows = ahuBoardApi.endpoints.getTunnelSectionList.select(tunnelId)(state).data;
  if (!rows) return;

  const entry = findTunnelListEntry(state, tunnelId);
  if (!entry) return;

  const length = sumSectionLengths(rows);
  if (Number(entry.tunnels.find((t) => t.id === tunnelId)?.length) === length) return;

  dispatch(
    ahuBoardApi.util.updateQueryData('getAhuTunnelList', entry.ahuId, (draft) =>
      draft.map((t) => (t.id === tunnelId ? { ...t, length } : t))
    ) as UpdateTunnelsAction
  );
}
